import React from 'react';
import { X, Image as ImageIcon, Eye } from 'lucide-react';
import { useTranslation } from '../../i18n/useTranslation';
import Modal from '../core/Modal';
import { formatTextWithStyles } from '../../utils/formatText';

/**
 * LandingPagePreviewModal - Preview the Group Landing Page as visitors will see it
 * Shows the final layout in a modal window for the group owner to review
 */
const LandingPagePreviewModal = ({
  isOpen,
  onClose,
  group,
  leftColumnContent,
  rightColumnContent,
  leftInlineImages,
  rightInlineImages
}) => {
  const { t } = useTranslation('groups');

  // Image size options: S = 33%, M = 66%, L = 100%
  const IMAGE_SIZES = {
    S: '33%',
    M: '66%',
    L: '100%'
  };

  // Render content with inline images (with size support)
  const renderContentWithImages = (content, images) => {
    if (!content) return null;

    // Split content by image markers
    const parts = content.split(/(\[IMG:[^\]]+\])/g);

    return parts.map((part, index) => {
      // Check if this is an image marker
      const imageMatch = part.match(/\[IMG:([^\]]+)\]/);
      if (imageMatch) {
        const imageId = imageMatch[1];
        const image = images.find(img => img.id === imageId);
        if (image) {
          const imageWidth = IMAGE_SIZES[image.size] || '100%';
          return (
            <div key={index} style={{ margin: '16px 0', position: 'relative' }}>
              <img
                src={image.displayUrl}
                alt=""
                loading="lazy"
                style={{
                  width: imageWidth,
                  borderRadius: '8px',
                  objectFit: 'cover'
                }}
              />
            </div>
          );
        }
        return null;
      }

      // Regular text content — through the articles' formatter (bold, italic, headings; React
      // text only, never HTML), keeping the line breaks the plain text had (addendum 2 item 2).
      if (part.trim()) {
        return (
          <React.Fragment key={index}>
            {formatTextWithStyles(part, `intro-${index}-`, {
              fontSize: '14px',
              lineHeight: '1.6',
              color: '#374151',
              whiteSpace: 'pre-wrap',
              marginBottom: '12px'
            })}
          </React.Fragment>
        );
      }
      return null;
    });
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      panelStyle={{
        backgroundColor: '#EDF2F8',
        borderRadius: '16px',
        width: '100%',
        maxWidth: '1200px',
        maxHeight: '90vh',
        overflow: 'hidden',
        display: 'flex',
        flexDirection: 'column',
        boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.25)',
        margin: '20px'
      }}
    >
        {/* Preview Header */}
        <div style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          padding: '16px 24px',
          backgroundColor: '#BB4D9A',
          color: 'white'
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
            <Eye size={20} />
            <span style={{ fontSize: '16px', fontWeight: '600' }}>
              {t('landingPagePreview.title')}
            </span>
          </div>
          <button
            onClick={onClose}
            style={{
              background: 'rgba(255, 255, 255, 0.2)',
              border: 'none',
              borderRadius: '8px',
              padding: '8px',
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              transition: 'background 0.2s ease'
            }}
            onMouseOver={(e) => e.currentTarget.style.background = 'rgba(255, 255, 255, 0.3)'}
            onMouseOut={(e) => e.currentTarget.style.background = 'rgba(255, 255, 255, 0.2)'}
          >
            <X size={20} color="white" />
          </button>
        </div>

        {/* Scrollable Content Area */}
        <div style={{
          flex: 1,
          overflow: 'auto',
          padding: '32px'
        }}>
          {/* Hero Section - Group Image and Title */}
          <div style={{
            display: 'flex',
            gap: '40px',
            marginBottom: '40px',
            alignItems: 'flex-start'
          }}>
            {/* Left - Group Image */}
            <div style={{
              width: '250px',
              flexShrink: 0,
              position: 'relative'
            }}>
              <div style={{
                width: '100%',
                aspectRatio: '1 / 1',
                borderRadius: '16px',
                overflow: 'hidden',
                backgroundColor: '#e5e7eb',
                position: 'relative'
              }}>
                {group?.cover_image_url ? (
                  <img
                    src={group.cover_image_url}
                    alt={group.name}
                    loading="lazy"
                    style={{
                      width: '100%',
                      height: '100%',
                      objectFit: 'cover'
                    }}
                  />
                ) : (
                  <div style={{
                    width: '100%',
                    height: '100%',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    backgroundColor: '#BB4D9A'
                  }}>
                    <ImageIcon size={48} color="white" />
                  </div>
                )}
              </div>
            </div>

            {/* Right - Group Title */}
            <div style={{ flex: 1, paddingTop: '16px' }}>
              <h1 style={{
                fontSize: '40px',
                fontWeight: '700',
                color: '#BB4D9A',
                lineHeight: '1.1',
                marginBottom: '12px'
              }}>
                {group?.name || t('landingPagePreview.groupNamePlaceholder')}
              </h1>
              {group?.description && (
                <p style={{
                  fontSize: '14px',
                  color: '#6b7280',
                  lineHeight: '1.6'
                }}>
                  {group.description}
                </p>
              )}
            </div>
          </div>

          {/* Two Column Content Area */}
          <div style={{
            display: 'flex',
            gap: '32px'
          }}>
            {/* Left Column - 65% */}
            <div style={{ flex: '0 0 65%' }}>
              <div style={{
                backgroundColor: 'white',
                borderRadius: '12px',
                padding: '24px',
                border: '1px solid #e5e7eb',
                minHeight: '200px'
              }}>
                {renderContentWithImages(leftColumnContent, leftInlineImages)}
                {!leftColumnContent && (
                  <p style={{ color: '#9ca3af', fontSize: '14px', fontStyle: 'italic' }}>
                    {t('landingPagePreview.noMainContent')}
                  </p>
                )}
              </div>
            </div>

            {/* Right Column - 35% */}
            <div style={{ flex: '0 0 35%' }}>
              <div style={{
                backgroundColor: 'white',
                borderRadius: '12px',
                padding: '24px',
                border: '1px solid #e5e7eb',
                minHeight: '200px'
              }}>
                {renderContentWithImages(rightColumnContent, rightInlineImages)}
                {!rightColumnContent && (
                  <p style={{ color: '#9ca3af', fontSize: '14px', fontStyle: 'italic' }}>
                    {t('landingPagePreview.noSecondaryContent')}
                  </p>
                )}
              </div>
            </div>
          </div>

          {/* Simulated Footer */}
          <div style={{
            marginTop: '32px',
            padding: '16px 24px',
            backgroundColor: 'white',
            borderRadius: '12px',
            border: '1px solid #e5e7eb',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            gap: '12px'
          }}>
            <div style={{
              padding: '10px 24px',
              background: '#BB4D9A',
              color: 'white',
              borderRadius: '20px',
              fontSize: '14px',
              fontWeight: '600',
              opacity: 0.6
            }}>
              {t('landingPagePreview.joinThisGroup')}
            </div>
            <div style={{
              padding: '10px 24px',
              background: 'white',
              color: '#BB4D9A',
              border: '2px solid #BB4D9A',
              borderRadius: '20px',
              fontSize: '14px',
              fontWeight: '600',
              opacity: 0.6
            }}>
              {t('landingPagePreview.continueToGroup')}
            </div>
            <span style={{ fontSize: '11px', color: '#9ca3af', marginLeft: '8px' }}>
              {t('landingPagePreview.buttonsPreviewOnly')}
            </span>
          </div>
        </div>

        {/* Close Button at bottom */}
        <div style={{
          padding: '16px 24px',
          borderTop: '1px solid #e5e7eb',
          display: 'flex',
          justifyContent: 'center'
        }}>
          <button
            onClick={onClose}
            style={{
              padding: '12px 32px',
              background: '#BB4D9A',
              color: 'white',
              border: 'none',
              borderRadius: '8px',
              fontSize: '14px',
              fontWeight: '600',
              cursor: 'pointer',
              transition: 'opacity 0.2s ease'
            }}
            onMouseOver={(e) => e.currentTarget.style.opacity = '0.9'}
            onMouseOut={(e) => e.currentTarget.style.opacity = '1'}
          >
            {t('landingPagePreview.closePreview')}
          </button>
        </div>
    </Modal>
  );
};

export default LandingPagePreviewModal;
